import React, { useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import logo from "../assets/logo.png";
import hero from "../assets/hero.png";

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    // Hero animation
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    // Content animations
    gsap.fromTo(
      contentRef.current.querySelectorAll(".animate-section"),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: contentRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <img src={logo} alt="Gift of God Fragrances" className="h-32 mx-auto mb-8" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#A88006]">
            About Gift of God Fragrances
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Discover the divine essence of luxury fragrances that elevate your senses and define your presence.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div ref={contentRef} className="max-w-6xl mx-auto px-4 py-16">
        {/* Our Story */}
        <div className="animate-section mb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-6">Our Story</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Founded with a passion for exceptional fragrances, Gift of God Fragrances has been crafting 
                premium scents that capture the essence of luxury and sophistication. Our journey began with 
                a simple belief: everyone deserves to experience the transformative power of beautiful fragrance.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                From our carefully curated collection of designer perfumes to our commitment to authentic, 
                high-quality products, we've built a reputation as a trusted destination for fragrance enthusiasts 
                worldwide.
              </p>
            </div>
            <div className="relative">
              <img 
                src={hero} 
                alt="Fragrance Collection" 
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="animate-section grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20">
            <h3 className="text-2xl font-bold text-[#A88006] mb-4">Our Mission</h3>
            <p className="text-gray-700 leading-relaxed">
              To provide authentic, premium fragrances at accessible prices while delivering exceptional 
              customer service and creating memorable experiences for every fragrance lover.
            </p>
          </div>
          <div className="bg-gradient-to-br from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20">
            <h3 className="text-2xl font-bold text-[#A88006] mb-4">Our Vision</h3>
            <p className="text-gray-700 leading-relaxed">
              To become the world's most trusted online destination for luxury fragrances, 
              inspiring confidence and elegance in every customer through the power of scent.
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="animate-section mb-16">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">✨</span>
              </div>
              <h4 className="text-xl font-semibold text-black mb-3">Authenticity</h4>
              <p className="text-gray-600">
                Every fragrance in our collection is 100% authentic, sourced directly from authorized distributors.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">🎯</span>
              </div>
              <h4 className="text-xl font-semibold text-black mb-3">Excellence</h4>
              <p className="text-gray-600">
                We strive for excellence in every aspect of our business, from product quality to customer service.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#A88006] to-[#9F7800] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">💎</span>
              </div>
              <h4 className="text-xl font-semibold text-black mb-3">Luxury</h4>
              <p className="text-gray-600">
                We believe luxury should be accessible, offering premium fragrances at competitive prices.
              </p>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="animate-section bg-gradient-to-r from-gray-50 to-gray-100 p-12 rounded-2xl border border-[#A88006]/20">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Why Choose Gift of God Fragrances?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl mb-4">🚚</div>
              <h4 className="text-lg font-semibold text-[#A88006] mb-2">Fast Shipping</h4>
              <p className="text-gray-600 text-sm">Free shipping on orders over $40 million</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-4">🔒</div>
              <h4 className="text-lg font-semibold text-[#A88006] mb-2">Secure Shopping</h4>
              <p className="text-gray-600 text-sm">SSL encrypted checkout for your safety</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-4">💯</div>
              <h4 className="text-lg font-semibold text-[#A88006] mb-2">100% Authentic</h4>
              <p className="text-gray-600 text-sm">Guaranteed authentic products only</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-4">🎁</div>
              <h4 className="text-lg font-semibold text-[#A88006] mb-2">Gift Ready</h4>
              <p className="text-gray-600 text-sm">Beautiful packaging for every order</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default About;